import { z } from "zod";
import { choiceSchema, type Value, type Field } from "./schema";
import { parseContext } from "./context";

const value = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.record(z.string(), z.unknown()),
  z.null(),
]);
/** A trusted context response, never a client-supplied choice override. Images use managed assets. */
export const dynamicChoiceCatalogSchema = z
  .object({
    kind: z.literal("dynamic-choices"),
    version: z.literal(1),
    choices: z
      .array(
        choiceSchema
          .omit({ image: true })
          .extend({
            value: z.string().min(1).max(32768),
            label: z.string().max(1000),
          })
          .strict(),
      )
      .max(1000),
    value: value.optional(),
  })
  .strict()
  .refine(
    (catalog) =>
      new Set(catalog.choices.map((choice) => choice.value)).size ===
      catalog.choices.length,
  );
export type DynamicChoiceCatalog = z.infer<typeof dynamicChoiceCatalogSchema>;
export const isDynamicChoiceField = (field: Pick<Field, "source">) =>
  field.source.kind === "dynamic" && field.source.dynamicMode === "choices";
export class DynamicChoiceError extends Error {
  constructor() {
    super("DYNAMIC_CHOICE_CATALOG_INVALID");
  }
}
export function parseDynamicChoiceCatalog(raw: unknown): DynamicChoiceCatalog {
  try {
    const copied = parseContext({ dynamic: { catalog: raw } }).dynamic!.catalog;
    const result = dynamicChoiceCatalogSchema.safeParse(copied);
    if (!result.success) throw new DynamicChoiceError();
    return structuredClone(result.data);
  } catch {
    throw new DynamicChoiceError();
  }
}
export function dynamicChoiceCatalog(
  choices: DynamicChoiceCatalog["choices"],
  value?: Value,
) {
  return parseDynamicChoiceCatalog({
    kind: "dynamic-choices",
    version: 1,
    choices,
    ...(value === undefined ? {} : { value }),
  });
}
